import { useCallback, useEffect, useState } from "react";
import { Activity, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/constants";

const ACTIONS = [
  "STATUS_CHANGED",
  "APPLICATION_CREATED",
  "APPLICATION_REASSIGNED",
  "NOTE_ADDED",
  "PROFILE_UPDATED",
  "JOB_CREATED",
  "JOB_UPDATED",
  "JOB_CLOSED",
  "JOB_DELETED",
  "MASTER_DATA_CREATED",
  "MASTER_DATA_UPDATED",
  "MASTER_DATA_DELETED",
  "RECRUITER_CREATED",
  "RECRUITER_UPDATED",
  "RECRUITER_ACTIVATED",
  "RECRUITER_DEACTIVATED",
  "MEMBER_CREATED",
  "LOGIN",
];

const ACTION_COLORS = {
  STATUS_CHANGED: "bg-blue-50 text-blue-700 border-blue-200",
  APPLICATION_CREATED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PROFILE_UPDATED: "bg-amber-50 text-amber-700 border-amber-200",
  LOGIN: "bg-slate-100 text-slate-600 border-slate-200",
  NOTE_ADDED: "bg-violet-50 text-violet-700 border-violet-200",
};

export default function AdminActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");
  const [entityType, setEntityType] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api
      .get("/activity-logs", { params: { action, entity_type: entityType, q: q || undefined } })
      .then((res) => setLogs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [action, entityType, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Activity Logs</h1>
        <p className="text-muted-foreground mt-1 text-sm">Complete audit trail of every action in the system.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="logs-search-input" placeholder="Search details or actor..." className="pl-9 bg-white" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-full sm:w-56 bg-white" data-testid="logs-action-filter">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a.replaceAll("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="logs-entity-filter">
            <SelectValue placeholder="Entity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            <SelectItem value="application">Application</SelectItem>
            <SelectItem value="job">Job</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="profile">Profile</SelectItem>
            <SelectItem value="master_data">Master Data</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : logs.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={Activity} title="No activity found" description="Try changing the filters." testId="logs-empty" />
        </Card>
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="activity-logs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="hover:bg-muted/60" data-testid="activity-log-row">
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full text-[10px] whitespace-nowrap ${ACTION_COLORS[log.action] || "bg-muted text-muted-foreground"}`}>
                        {log.action.replaceAll("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm max-w-md">{log.details}</TableCell>
                    <TableCell>
                      <p className="text-sm">{log.actor_name}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">{log.actor_role}</p>
                    </TableCell>
                    <TableCell className="text-xs font-mono whitespace-nowrap">{formatDateTime(log.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
